import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, 
  Smartphone, 
  Building2, 
  CreditCard,
  Copy, 
  Check,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Stripe Payment Links (productos pre-creados en modo LIVE)
const STRIPE_PAYMENT_LINKS = {
  50: 'https://buy.stripe.com/fZu9AU17VbGUgdxeIfcEw00',
  100: 'https://buy.stripe.com/4gMeVecQD4esbXhbw3cEw01',
  300: 'https://buy.stripe.com/7sY7sM3g34es8L58jRcEw02',
  500: 'https://buy.stripe.com/9B69AU4k712g2mHfMjcEw03',
  1000: 'https://buy.stripe.com/00w28s7wjaCQ3qL2ZxcEw04'
};

// Card payment amounts (products)
const CARD_AMOUNTS = [50, 100, 300, 500, 1000];

export default function Deposit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [selectedCardAmount, setSelectedCardAmount] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [copied, setCopied] = useState('');
  const [success, setSuccess] = useState(false);
  const [campaignAmount, setCampaignAmount] = useState(null);

  // Check if account is pending
  const isPending = user?.status === 'pending';

  useEffect(() => {
    fetchPaymentInfo();
    
    // Check for pre-loaded amount from campaign
    const urlAmount = searchParams.get('amount');
    const campaignId = searchParams.get('campaign');
    if (urlAmount && campaignId) {
      const parsedAmount = parseFloat(urlAmount);
      setCampaignAmount(parsedAmount);
      setAmount(urlAmount);
      // Auto-select bank transfer for campaign deposits (usually larger amounts)
      setPaymentMethod('transferencia');
      toast.success(t('campaigns.preloadedAmount', { amount: parsedAmount.toLocaleString('pt-PT') }));
    }
  }, [searchParams, t]);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/deposits/payment-info`);
      setPaymentInfo(response.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch payment info:', error);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
    toast.success(t('deposits.copiedToClipboard'));
  };

  // If account is pending, show restriction message
  if (isPending) {
    return (
      <div className="max-w-lg mx-auto" data-testid="deposit-restricted">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.back')}
        </button>

        <div className="glass-card p-8 text-center">
          <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">{t('deposits.accountUnderReview')}</h2>
          <p className="text-slate-400 mb-6">
            {t('deposits.depositsAvailableAfterApproval')}
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl px-8"
          >
            {t('deposits.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Handle card payment with Stripe Payment Links
    if (paymentMethod === 'card') {
      if (!selectedCardAmount) {
        toast.error(t('deposits.selectAmountToPay'));
        return;
      }
      
      setCardLoading(true);
      try {
        // Create pending deposit record first
        const response = await axios.post(`${API_URL}/deposits/stripe/create-pending`, {
          amount: selectedCardAmount
        });
        
        const depositId = response.data.deposit_id;
        
        // Build Stripe Payment Link with client_reference_id
        const baseLink = STRIPE_PAYMENT_LINKS[selectedCardAmount];
        const successUrl = encodeURIComponent(`${window.location.origin}/payment/success?deposit_id=${depositId}`);
        const stripeUrl = `${baseLink}?client_reference_id=${depositId}&prefilled_email=${encodeURIComponent(user?.email || '')}&success_url=${successUrl}`;
        
        // Redirect to Stripe
        window.location.href = stripeUrl;
      } catch (error) {
        toast.error(error.response?.data?.detail || t('deposits.errorStartingPayment'));
        setCardLoading(false);
      }
      return;
    }

    // For MBWay and Transfer, validate amount
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('deposits.enterValidAmount'));
      return;
    }
    
    if (!paymentMethod) {
      toast.error(t('deposits.selectPaymentMethod'));
      return;
    }

    // Handle MBWay and Transfer
    setLoading(true);
    try {
      await axios.post(`${API_URL}/deposits`, {
        amount: parseFloat(amount),
        payment_method: paymentMethod
      });
      setSuccess(true);
      toast.success(t('deposits.depositSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('deposits.errorProcessingDeposit'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto animate-fade-in" data-testid="deposit-success">
        <div className="glass-card p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2 font-['Outfit']">
            {t('deposits.requestSent')}
          </h2>
          <p className="text-slate-400 mb-6">
            {t('deposits.depositRequestOf')} <span className="text-white font-semibold">€{parseFloat(amount).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span> {t('deposits.wasReceived')}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-500 hover:bg-blue-600 rounded-xl"
            >
              {t('deposits.backToDashboard')}
            </Button>
            <Button
              onClick={() => navigate('/deposits')}
              variant="outline"
              className="bg-white/5 hover:bg-white/10 border-white/10 rounded-xl"
            >
              {t('deposits.viewDeposits')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="deposit-page">
      {/* Campaign Banner */}
      {campaignAmount && (
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-amber-500/20 via-orange-500/10 to-amber-500/20 border border-amber-500/30" data-testid="campaign-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Gift className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-amber-400 font-semibold">{t('deposits.activePromotion')}</p>
              <p className="text-slate-300 text-sm">
                {t('campaigns.preloadedAmount', { amount: campaignAmount.toLocaleString('pt-PT') })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('deposits.addFunds')}</h1>
          <p className="text-slate-400 text-sm">{t('deposits.selectAmountAndMethod')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Payment Method Selection */}
        <div className="glass-card p-6">
          <Label className="text-slate-300 mb-4 block">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-sm font-bold mr-2">1</span>
            {t('deposits.howToPay')}
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => { setPaymentMethod('mbway'); setSelectedCardAmount(null); }}
              className={`p-5 rounded-2xl border-2 transition-all text-left ${
                paymentMethod === 'mbway'
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              data-testid="payment-mbway"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  paymentMethod === 'mbway' ? 'bg-blue-500/20' : 'bg-white/10'
                }`}>
                  <Smartphone className={`w-5 h-5 ${paymentMethod === 'mbway' ? 'text-blue-400' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-white font-semibold">MB Way</p>
                  <p className="text-xs text-slate-400">{t('deposits.instant')}</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => { setPaymentMethod('transferencia'); setSelectedCardAmount(null); }}
              className={`p-5 rounded-2xl border-2 transition-all text-left ${
                paymentMethod === 'transferencia'
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              data-testid="payment-transfer"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  paymentMethod === 'transferencia' ? 'bg-blue-500/20' : 'bg-white/10'
                }`}>
                  <Building2 className={`w-5 h-5 ${paymentMethod === 'transferencia' ? 'text-blue-400' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-white font-semibold">{t('deposits.transfer')}</p>
                  <p className="text-xs text-slate-400">{t('deposits.days12')}</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => { setPaymentMethod('card'); setAmount(''); }}
              className={`p-5 rounded-2xl border-2 transition-all text-left ${
                paymentMethod === 'card'
                  ? 'border-emerald-500 bg-emerald-500/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              data-testid="payment-card"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  paymentMethod === 'card' ? 'bg-emerald-500/20' : 'bg-white/10'
                }`}>
                  <CreditCard className={`w-5 h-5 ${paymentMethod === 'card' ? 'text-emerald-400' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-white font-semibold">{t('deposits.card')}</p>
                  <p className="text-xs text-slate-400">{t('deposits.visaMc')}</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Step 2: Amount Selection - Only show after payment method is selected */}
        {paymentMethod && (
          <div className="glass-card p-6 animate-fade-in">
            <Label className="text-slate-300 mb-4 block">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-sm font-bold mr-2">2</span>
              {paymentMethod === 'card' ? t('deposits.selectAmount') : t('deposits.enterAmount')}
            </Label>
            
            {/* Card: Show predefined amounts */}
            {paymentMethod === 'card' ? (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
                {CARD_AMOUNTS.map((cardAmount) => (
                  <button
                    key={cardAmount}
                    type="button"
                    onClick={() => setSelectedCardAmount(cardAmount)}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all text-center ${
                      selectedCardAmount === cardAmount
                        ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400 scale-105'
                        : 'border-white/10 bg-white/5 hover:border-white/20 text-white hover:scale-102'
                    }`}
                  >
                    <span className="text-base sm:text-lg font-bold">€{cardAmount}</span>
                  </button>
                ))}
              </div>
            ) : (
              /* MB Way / Transfer: Show free amount input */
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-slate-400">€</span>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="pl-12 h-16 text-2xl bg-[#161C22] border-white/10 text-white rounded-xl focus:border-blue-500"
                  data-testid="deposit-amount"
                />
              </div>
            )}
          </div>
        )}

        {/* Payment Details */}
        {paymentMethod && paymentMethod !== 'card' && paymentInfo && (
          <div className="glass-card p-6 animate-fade-in" data-testid="payment-details">
            <div className="flex items-center gap-2 text-amber-400 mb-4">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{t('deposits.paymentData')}</span>
            </div>

            {paymentMethod === 'mbway' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-400">{t('deposits.mbwayNumber')}</p>
                    <p className="text-xl text-white font-mono">{paymentInfo.mbway.phone}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(paymentInfo.mbway.phone, 'phone')}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copied === 'phone' ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {paymentMethod === 'transferencia' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-400">{t('deposits.beneficiary')}</p>
                    <p className="text-lg text-white font-semibold">{paymentInfo.transferencia.beneficiary}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(paymentInfo.transferencia.beneficiary, 'beneficiary')}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copied === 'beneficiary' ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-400">IBAN</p>
                    <p className="text-lg text-white font-mono">{paymentInfo.transferencia.iban}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(paymentInfo.transferencia.iban.replace(/\s/g, ''), 'iban')}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copied === 'iban' ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-400">{t('deposits.bicSwift')}</p>
                    <p className="text-lg text-white font-mono">{paymentInfo.transferencia.bic_swift}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(paymentInfo.transferencia.bic_swift, 'bic')}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copied === 'bic' ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-sm text-slate-400">{t('deposits.bank')}</p>
                  <p className="text-white font-semibold">{paymentInfo.transferencia.bank}</p>
                </div>
              </div>
            )}

            <p className="text-sm text-slate-500 mt-4">
              {t('deposits.afterPayment')}
            </p>
          </div>
        )}

        {/* Card Payment Info - Security badge */}
        {paymentMethod === 'card' && selectedCardAmount && (
          <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20 animate-fade-in">
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <span>🔒</span>
              <span>{t('deposits.securePayment')}</span>
            </div>
          </div>
        )}

        {/* Submit Button - Only show when ready */}
        {paymentMethod && (paymentMethod === 'card' ? selectedCardAmount : amount) && (
          <Button
            type="submit"
            disabled={loading || cardLoading}
            className={`w-full h-14 text-white rounded-xl font-semibold text-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed ${
              paymentMethod === 'card' 
                ? 'bg-emerald-500 hover:bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.3)]' 
                : 'bg-blue-500 hover:bg-blue-600 shadow-[0_0_20px_rgba(59,130,246,0.3)]'
            }`}
            data-testid="deposit-submit"
          >
            {loading || cardLoading ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {cardLoading ? t('deposits.redirecting') : t('deposits.processing')}
              </div>
            ) : paymentMethod === 'card' ? (
              `${t('deposits.pay')} €${selectedCardAmount}`
            ) : (
              `${t('deposits.confirmPayment')} €${parseFloat(amount || 0).toFixed(2)}`
            )}
          </Button>
        )}
      </form>
    </div>
  );
}
